import React from 'react';
import { fmt, money, BASIS_UNIT } from './aiqMeta';
import { InvoiceBasisSelect } from './InvoiceBasisSelect';

const qty = (n) => fmt(n, Number.isInteger(Number(n)) ? 0 : 2);

const Qty = ({ side, highlight }) => (
  <div className={`font-mono text-xs leading-5 ${highlight ? 'text-amber-800' : 'text-zinc-700'}`}>
    <div>{qty(side.cases)} cs</div>
    <div>{fmt(side.kg, 3)} kg</div>
    <div className="text-zinc-400">{qty(side.units)} units</div>
  </div>
);

const ProductCell = ({ line }) => (
  <td className="px-3 py-2.5 align-top">
    <div className="text-sm font-medium text-zinc-900">{line.product_name}</div>
    <div className="text-xs text-zinc-500 mt-0.5">
      <span className="font-mono">{line.stock_code || 'no stock code'}</span>
      {line.description && line.description !== line.product_name && <span> · {line.description}</span>}
    </div>
    <div className="text-[11px] text-zinc-400 mt-1">
      {line.batches.map((b) => `${b.batch_code || 'no batch'} · ${fmt(b.kg, 3)} kg`).join('  |  ')}
    </div>
  </td>
);

const LineNotes = ({ line, colSpan }) => {
  const flags = line.variation?.flags || [];
  if (!line.issues.length && !flags.length) return null;
  return (
    <tr className="bg-zinc-50/60">
      <td colSpan={colSpan} className="px-3 pb-2.5 pt-0 text-xs" data-testid={`aiq-preview-issues-${line.product_id}`}>
        {line.issues.map((t) => <div key={t} className="text-red-700">● {t}</div>)}
        {flags.map((t) => <div key={t} className="text-amber-800">▲ {t}</div>)}
      </td>
    </tr>
  );
};

const LineRow = ({ line, currency, onBasis, busy }) => (
  <>
    <tr className={line.issues.length ? 'bg-red-50/30' : ''} data-testid={`aiq-preview-line-${line.product_id}`}>
      <ProductCell line={line} />
      <td className="px-3 py-2.5 align-top"><Qty side={line.ordered} /></td>
      <td className="px-3 py-2.5 align-top"><Qty side={line.dispatched} highlight={line.variation?.flags?.length > 0} /></td>
      <td className="px-3 py-2.5 align-top">
        <InvoiceBasisSelect value={line.basis} onChange={(b) => onBasis(line, b)} disabled={busy || !line.product_id}
          testId={`aiq-preview-basis-${line.product_id}`} />
      </td>
      <td className="px-3 py-2.5 align-top text-right font-mono text-sm font-semibold text-zinc-900" data-testid={`aiq-preview-qty-${line.product_id}`}>
        {line.basis ? `${fmt(line.quantity, line.basis === 'kg' ? 3 : 0)} ${BASIS_UNIT[line.basis]}` : '—'}
      </td>
      <td className="px-3 py-2.5 align-top text-right" data-testid={`aiq-preview-price-${line.product_id}`}>
        <div className="font-mono text-sm">{line.unit_price == null ? '—' : fmt(line.unit_price, 2)}</div>
        <div className="text-[10px] text-zinc-500">{line.price_source || 'no price'}</div>
      </td>
      <td className="px-3 py-2.5 align-top text-right font-mono text-xs">
        {line.tax_code ? `${line.tax_code} · ${line.tax_rate == null ? '?' : fmt(line.tax_rate, 1)}%` : '—'}
      </td>
      <td className="px-3 py-2.5 align-top text-right font-mono text-sm font-semibold" data-testid={`aiq-preview-net-${line.product_id}`}>
        {money(line.net, currency)}
      </td>
    </tr>
    <LineNotes line={line} colSpan={8} />
  </>
);

export const InvoiceLinesTable = ({ lines, currency, onBasis, busy }) => (
  <div className="border rounded-lg overflow-x-auto" data-testid="aiq-preview-lines">
    <table className="w-full">
      <thead>
        <tr className="bg-zinc-100 text-[11px] uppercase tracking-wide text-zinc-600 text-left">
          <th className="px-3 py-2">Product &amp; batches</th><th className="px-3 py-2">Ordered</th>
          <th className="px-3 py-2">Shipped</th><th className="px-3 py-2">Invoice by</th>
          <th className="px-3 py-2 text-right">Invoiced qty</th><th className="px-3 py-2 text-right">Unit price</th>
          <th className="px-3 py-2 text-right">Tax</th><th className="px-3 py-2 text-right">Net</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-zinc-100">
        {lines.map((ln) => <LineRow key={ln.product_id || ln.product_name} line={ln} currency={currency} onBasis={onBasis} busy={busy} />)}
        {lines.length === 0 && (
          <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-zinc-500">No invoiceable lines on this order.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);
